import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { postsApi } from '@/services/api/postsApi';
import { extractError } from '@/lib/extractError';
import { isBillingBlockError } from '@/lib/subscription';
import { notifySetupProgress } from '@/lib/setupEvents';
import type {
  Post,
  PostStats,
  ListPostsParams,
  ScheduleParams,
  UpdatePostDto,
  ContentGenerationDto,
  PromptContentGenerationDto,
} from '@/types';

interface PostsState {
  /** All fetched posts (paginated) */
  posts: Post[];
  /** Posts filtered by pending_approval status */
  pendingPosts: Post[];
  /** Scheduled posts for calendar view */
  scheduledPosts: Post[];
  /** Post statistics */
  stats: PostStats | null;
  /** Pagination info */
  page: number;
  totalPages: number;
  total: number;
  /** Loading states */
  isLoading: boolean;
  isLoadingPending: boolean;
  isLoadingSchedule: boolean;
  isLoadingStats: boolean;
  /** Error state */
  error: string | null;
}

interface PostsActions {
  /** Fetch paginated posts with optional filters */
  fetchPosts: (params?: ListPostsParams) => Promise<void>;
  /** Fetch posts with pending_approval status */
  fetchPendingPosts: (params?: ListPostsParams) => Promise<void>;
  /** Fetch scheduled posts for a date range */
  fetchScheduledPosts: (params: ScheduleParams) => Promise<void>;
  /** Fetch post statistics */
  fetchStats: () => Promise<void>;
  /** Approve a single post */
  approvePost: (postId: string) => Promise<void>;
  /** Reject a post (updates status via PATCH) */
  rejectPost: (postId: string) => Promise<void>;
  /** Delete a post permanently */
  deletePost: (postId: string) => Promise<void>;
  /** Update a post */
  updatePost: (postId: string, data: UpdatePostDto) => Promise<void>;
  /** Reschedule a post — optimistic update, rollback on failure */
  reschedulePost: (postId: string, scheduledFor: string) => Promise<void>;
  /** Cancel a scheduled post — optimistically removes from calendar, rollback on failure */
  cancelPost: (postId: string) => Promise<void>;
  /** Bulk approve posts */
  bulkApprovePosts: (postIds: string[]) => Promise<void>;
  /** Generate content via AI */
  generateContent: (data: ContentGenerationDto) => Promise<string | null>;
  /** Generate content from prompt */
  generateFromPrompt: (data: PromptContentGenerationDto) => Promise<string | null>;
  /** Poll a content generation job; returns a cancel function to stop polling */
  pollJob: (jobId: string, onComplete?: (posts: Post[]) => void) => () => void;
  /** Clear error */
  clearError: () => void;
}

export const usePostsStore = create<PostsState & PostsActions>()(
  devtools(
    (set, get) => ({
      posts: [],
      pendingPosts: [],
      scheduledPosts: [],
      stats: null,
      page: 1,
      totalPages: 1,
      total: 0,
      isLoading: false,
      isLoadingPending: false,
      isLoadingSchedule: false,
      isLoadingStats: false,
      error: null,

      fetchPosts: async params => {
        set({ isLoading: true, error: null }, false, 'posts/fetchPosts');
        try {
          const result = await postsApi.list(params);
          set(
            {
              posts: result.data ?? [],
              page: result.page ?? 1,
              totalPages: result.totalPages ?? 1,
              total: result.total ?? 0,
              isLoading: false,
            },
            false,
            'posts/fetchPosts/success'
          );
        } catch (err) {
          set({ isLoading: false, error: extractError(err) }, false, 'posts/fetchPosts/error');
        }
      },

      fetchPendingPosts: async params => {
        set({ isLoadingPending: true, error: null }, false, 'posts/fetchPending');
        try {
          // Defaults for pending posts — not paginated in the UI
          const result = await postsApi.list({
            page: 1,
            limit: 20,
            ...params,
            status: 'pending_approval',
          });
          set(
            { pendingPosts: result.data ?? [], isLoadingPending: false },
            false,
            'posts/fetchPending/success'
          );
        } catch (err) {
          set(
            { isLoadingPending: false, error: extractError(err) },
            false,
            'posts/fetchPending/error'
          );
        }
      },

      fetchScheduledPosts: async params => {
        set({ isLoadingSchedule: true, error: null }, false, 'posts/fetchSchedule');
        try {
          // Always fetch via the regular list endpoint and filter by date
          // range client-side. The dedicated /posts/schedule endpoint only
          // returns status === 'scheduled' (see BE post.repository.getSchedule),
          // which would hide review-queue / pending_approval posts that
          // LIL-282 specifically wants on the calendar in grey. Running
          // both endpoints and short-circuiting on the schedule one
          // caused drafts to disappear as soon as the user had any
          // scheduled post in the same month.
          const result = await postsApi.list({
            page: 1,
            limit: 100,
            sortBy: 'createdAt',
            sortOrder: 'desc',
          });
          const allPosts = result.data ?? [];
          const start = new Date(params.startDate).getTime();
          const end = new Date(params.endDate).getTime();

          const inRangePosts = allPosts.filter(p => {
            const ref = p.scheduledFor ?? p.createdAt;
            if (!ref) return false;
            const t = new Date(ref).getTime();
            return t >= start && t <= end;
          });

          // Exclude scraped/imported content (source === "existing_profile").
          // The calendar is for Lili-scheduled posts, not historical posts
          // pulled from the user's connected profile. See LIL-248.
          const calendarPosts = inRangePosts.filter(p => p.source !== 'existing_profile');

          set(
            { scheduledPosts: calendarPosts, isLoadingSchedule: false },
            false,
            'posts/fetchSchedule/success'
          );
        } catch (err) {
          set(
            { isLoadingSchedule: false, error: extractError(err) },
            false,
            'posts/fetchSchedule/error'
          );
        }
      },

      fetchStats: async () => {
        set({ isLoadingStats: true }, false, 'posts/fetchStats');
        try {
          const stats = await postsApi.getStats();
          set({ stats, isLoadingStats: false }, false, 'posts/fetchStats/success');
        } catch (err) {
          set({ isLoadingStats: false, error: extractError(err) }, false, 'posts/fetchStats/error');
        }
      },

      approvePost: async postId => {
        try {
          const updated = await postsApi.approve(postId);
          set(
            state => ({
              pendingPosts: state.pendingPosts.filter(p => p._id !== postId),
              posts: state.posts.map(p => (p._id === postId ? updated : p)),
              scheduledPosts: state.scheduledPosts.map(p => (p._id === postId ? updated : p)),
            }),
            false,
            'posts/approve'
          );
          notifySetupProgress('review-queue', true);
        } catch (err) {
          set({ error: extractError(err) }, false, 'posts/approve/error');
          throw err;
        }
      },

      rejectPost: async postId => {
        try {
          const updated = await postsApi.update(postId, {
            status: 'rejected',
          });
          set(
            state => ({
              pendingPosts: state.pendingPosts.filter(p => p._id !== postId),
              posts: state.posts.map(p => (p._id === postId ? updated : p)),
              scheduledPosts: state.scheduledPosts.map(p => (p._id === postId ? updated : p)),
            }),
            false,
            'posts/reject'
          );
          notifySetupProgress('review-queue', true);
        } catch (err) {
          set({ error: extractError(err) }, false, 'posts/reject/error');
          throw err;
        }
      },

      deletePost: async postId => {
        try {
          // Try DELETE first; fall back to PATCH status:'rejected' until BE adds the endpoint
          try {
            await postsApi.delete(postId);
          } catch {
            await postsApi.update(postId, { status: 'rejected' });
          }
          set(
            state => ({
              pendingPosts: state.pendingPosts.filter(p => p._id !== postId),
              posts: state.posts.filter(p => p._id !== postId),
              scheduledPosts: state.scheduledPosts.filter(p => p._id !== postId),
            }),
            false,
            'posts/delete'
          );
        } catch (err) {
          set({ error: extractError(err) }, false, 'posts/delete/error');
          throw err;
        }
      },

      updatePost: async (postId, data) => {
        try {
          const updated = await postsApi.update(postId, data);
          set(
            state => ({
              posts: state.posts.map(p => (p._id === postId ? updated : p)),
              pendingPosts: state.pendingPosts.map(p => (p._id === postId ? updated : p)),
              scheduledPosts: state.scheduledPosts.map(p => (p._id === postId ? updated : p)),
            }),
            false,
            'posts/update'
          );
        } catch (err) {
          set({ error: extractError(err) }, false, 'posts/update/error');
          throw err;
        }
      },

      reschedulePost: async (postId, scheduledFor) => {
        const { posts, pendingPosts, scheduledPosts } = get();

        // Only posts already pushed to PostForMe (status === 'scheduled') need
        // the re-sync endpoint. For approved/pending_approval/draft posts there
        // is no external job yet, so a plain PATCH is correct (and the
        // /reschedule endpoint would 400 with "Only scheduled posts can be
        // rescheduled").
        const current =
          scheduledPosts.find(p => p._id === postId) ??
          posts.find(p => p._id === postId) ??
          pendingPosts.find(p => p._id === postId);
        const needsExternalResync = current?.status === 'scheduled';

        const patch = (p: Post) => (p._id === postId ? { ...p, scheduledFor } : p);

        set(
          {
            posts: posts.map(patch),
            pendingPosts: pendingPosts.map(patch),
            scheduledPosts: scheduledPosts.map(patch),
          },
          false,
          'posts/reschedule/optimistic'
        );

        try {
          const updated = needsExternalResync
            ? await postsApi.reschedule(postId, scheduledFor)
            : await postsApi.update(postId, { scheduledFor });
          set(
            state => ({
              posts: state.posts.map(p => (p._id === postId ? updated : p)),
              pendingPosts: state.pendingPosts.map(p => (p._id === postId ? updated : p)),
              scheduledPosts: state.scheduledPosts.map(p => (p._id === postId ? updated : p)),
            }),
            false,
            'posts/reschedule/success'
          );
        } catch (err) {
          set(
            {
              posts,
              pendingPosts,
              scheduledPosts,
              error: extractError(err),
            },
            false,
            'posts/reschedule/rollback'
          );
          throw err;
        }
      },

      cancelPost: async postId => {
        const { posts, pendingPosts, scheduledPosts } = get();

        // Only posts already pushed to PostForMe (status === 'scheduled') have
        // an external job that needs tearing down. For other statuses we just
        // flip local status — the unschedule endpoint would 400 with "Post is
        // not scheduled".
        const current =
          scheduledPosts.find(p => p._id === postId) ??
          posts.find(p => p._id === postId) ??
          pendingPosts.find(p => p._id === postId);
        const needsExternalTeardown = current?.status === 'scheduled';

        // Optimistically drop from the calendar list — a cancelled post should
        // not occupy a slot regardless of what the BE returns.
        set(
          { scheduledPosts: scheduledPosts.filter(p => p._id !== postId) },
          false,
          'posts/cancel/optimistic'
        );

        // httpClient's error interceptor rejects with the backend envelope
        // ({ data, error, meta: { statusCode }, timestamp }), so the status
        // lives at err.meta.statusCode.
        const statusOf = (err: unknown) =>
          (err as { meta?: { statusCode?: number } })?.meta?.statusCode ??
          (err as { response?: { status?: number } })?.response?.status ??
          (err as { status?: number })?.status;

        try {
          if (needsExternalTeardown) {
            try {
              await postsApi.unschedule(postId);
            } catch (err) {
              // 400 "Post is not scheduled" — already unscheduled externally,
              // safe to continue to the PATCH that flips local status.
              if (statusOf(err) !== 400) throw err;
            }
          }
          const updated = await postsApi.update(postId, { status: 'draft' });
          set(
            state => ({
              posts: state.posts.map(p => (p._id === postId ? updated : p)),
              pendingPosts: state.pendingPosts.filter(p => p._id !== postId),
            }),
            false,
            'posts/cancel/success'
          );
        } catch (err) {
          set(
            {
              posts,
              pendingPosts,
              scheduledPosts,
              error: extractError(err),
            },
            false,
            'posts/cancel/rollback'
          );
          throw err;
        }
      },

      bulkApprovePosts: async postIds => {
        try {
          await postsApi.bulkApprove({ postIds });
          set(
            state => ({
              pendingPosts: state.pendingPosts.filter(p => !postIds.includes(p._id)),
              posts: state.posts.map(p =>
                postIds.includes(p._id) ? { ...p, status: 'approved' } : p
              ),
              scheduledPosts: state.scheduledPosts.map(p =>
                postIds.includes(p._id) ? { ...p, status: 'approved' } : p
              ),
            }),
            false,
            'posts/bulkApprove'
          );
          notifySetupProgress('review-queue', true);
        } catch (err) {
          set({ error: extractError(err) }, false, 'posts/bulkApprove/error');
          throw err;
        }
      },

      generateContent: async data => {
        try {
          const result = await postsApi.generateContent(data);
          return result.jobs?.[0]?.jobId ?? null;
        } catch (err) {
          set({ error: extractError(err) }, false, 'posts/generate/error');
          return null;
        }
      },

      generateFromPrompt: async data => {
        try {
          const result = await postsApi.generateFromPrompt(data);
          return result.jobs?.[0]?.jobId ?? null;
        } catch (err) {
          // A 402 billing block is already surfaced globally (UpgradeModal /
          // Paywall) by the httpClient interceptor. Re-throw so callers can
          // skip their own generic error toast instead of double-messaging.
          if (isBillingBlockError(err)) throw err;
          set({ error: extractError(err) }, false, 'posts/generatePrompt/error');
          return null;
        }
      },

      pollJob: (jobId, onComplete) => {
        let retries = 0;
        let cancelled = false;
        const MAX_RETRIES = 60; // ~3 minutes at 3s intervals

        const isTerminal = (job: { status: string; progress?: number }) =>
          job.status === 'completed' || (job.status === 'active' && (job.progress ?? 0) >= 100);

        const delay = (ms: number) => new Promise<void>(r => setTimeout(r, ms));

        const safeComplete = (posts: Post[]) => {
          if (!cancelled) onComplete?.(posts);
        };

        const poll = async () => {
          if (cancelled) return;
          if (retries++ >= MAX_RETRIES) {
            set({ error: 'Content generation timed out' }, false, 'posts/pollJob/timeout');
            safeComplete([]);
            return;
          }
          try {
            const job = await postsApi.getJobStatus(jobId);
            if (cancelled) return;
            if (isTerminal(job)) {
              // job.result is a ContentWriterResult { postId, ... }, not Post[].
              // Fetch the actual post by ID when available.
              const fetchByJobPostId = async (postId: string) => {
                try {
                  return await postsApi.getById(postId);
                } catch {
                  return null;
                }
              };

              if (job.result?.postId) {
                const post = await fetchByJobPostId(job.result.postId);
                if (cancelled) return;
                if (post) {
                  safeComplete([post]);
                  return;
                }
              }

              // Fallback: re-poll job status up to 3 times in case the
              // postId hasn't been written yet. Do NOT fetch recent pending
              // posts — that would return unrelated posts and cause duplicates.
              for (let attempt = 0; attempt < 3; attempt++) {
                if (cancelled) return;
                await delay(2000);
                if (cancelled) return;
                const retryJob = await postsApi.getJobStatus(jobId);
                if (cancelled) return;
                if (retryJob.result?.postId) {
                  const post = await fetchByJobPostId(retryJob.result.postId);
                  if (cancelled) return;
                  if (post) {
                    safeComplete([post]);
                    return;
                  }
                }
              }
              safeComplete([]);
              return;
            }
            if (job.status === 'failed') {
              set(
                { error: job.error ?? 'Content generation failed' },
                false,
                'posts/pollJob/failed'
              );
              safeComplete([]);
              return;
            }
            // Still processing — retry after 3s
            if (!cancelled) {
              setTimeout(poll, 3000);
            }
          } catch (err) {
            if (cancelled) return;
            set({ error: extractError(err) }, false, 'posts/pollJob/error');
            safeComplete([]);
          }
        };
        poll();
        return () => {
          cancelled = true;
        };
      },

      clearError: () => set({ error: null }, false, 'posts/clearError'),
    }),
    {
      name: 'PostsStore',
      enabled: import.meta.env.DEV,
    }
  )
);
